import { Card, Inline, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { BUILT_IN_ROLES, type Role } from "../access/roles.js";
import { usePlatformOwner } from "../access/platform-owner.js";
import { useSession } from "../session/SessionProvider.js";

/**
 * Sprint 24 Wave E PR-E5 — read-only redacted permission
 * diagnostics page.
 *
 * Renders the built-in role catalog and the derived permission
 * set per role. No raw `sub` / token / cookie / Keycloak
 * credential exposure (Wave A KEYCLOAK_PROVISIONING_STRATEGY.md
 * §6).
 *
 * Sprint 25 PR-A3F2 — adds a "current session" panel above the
 * catalog so the operator can answer "I'm signed in as owner but
 * the page says platform-owner only — what does my session
 * actually carry?" without leaving the Admin Console. The panel
 * shows ONLY the session fields that are already public on other
 * surfaces (account menu, tenant selector):
 *
 *   - email + displayName (already shown in the account menu);
 *   - tenantId (UUID, already in URLs and on the tenant picker);
 *   - the role array verbatim (already used for UI gating);
 *   - a derived "platform.owner present?" yes/no chip.
 *
 * NEVER:
 *   - the JWT,
 *   - the session cookie,
 *   - the Keycloak `sub` (the SessionProvider keeps `sub` in scope
 *     for callbacks but this page never reads it — pinned by
 *     test).
 */
export function PermissionsDiagnosticsPage() {
  const { t } = useLocale();
  const session = useSession();
  const platformOwner = usePlatformOwner(session);
  const roles = Object.values(BUILT_IN_ROLES) as Role[];

  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <h2
          style={{
            fontSize: "var(--gt-sys-font-size-2xl)",
            fontWeight: "var(--gt-sys-font-weight-bold)",
            margin: 0,
          }}
        >
          {t("admin.permissions_diagnostics.title")}
        </h2>
        <p style={{ color: "var(--gt-sys-text-muted)", margin: 0 }}>
          {t("admin.permissions_diagnostics.subtitle")}
        </p>
      </Stack>
      <Card elevation={1} data-testid="admin-permissions-diagnostics-current-session">
        <Stack gap={2}>
          <strong style={{ fontSize: "var(--gt-sys-font-size-lg)" }}>
            {t("admin.permissions_diagnostics.current_session.title")}
          </strong>
          <p
            style={{
              color: "var(--gt-sys-text-muted)",
              fontSize: "var(--gt-sys-font-size-sm)",
              margin: 0,
            }}
          >
            {t("admin.permissions_diagnostics.current_session.body")}
          </p>
          <Stack gap={1}>
            <SessionRow
              label={t("admin.permissions_diagnostics.current_session.email")}
              value={session.identity.email}
              testId="admin-perm-current-session-email"
            />
            <SessionRow
              label={t("admin.permissions_diagnostics.current_session.display_name")}
              value={session.identity.displayName ?? "—"}
              testId="admin-perm-current-session-display-name"
            />
            <SessionRow
              label={t("admin.permissions_diagnostics.current_session.tenant_id")}
              value={session.tenantId ?? "—"}
              testId="admin-perm-current-session-tenant-id"
              mono
            />
            <Inline gap={2} align="center">
              <span
                style={{
                  fontSize: "var(--gt-sys-font-size-sm)",
                  fontWeight: "var(--gt-sys-font-weight-semibold)",
                }}
              >
                {t("admin.permissions_diagnostics.current_session.platform_owner_label")}
              </span>
              <span
                data-testid="admin-perm-current-session-platform-owner-flag"
                style={{
                  display: "inline-block",
                  padding: "2px 10px",
                  borderRadius: 999,
                  fontSize: "var(--gt-sys-font-size-xs)",
                  fontWeight: "var(--gt-sys-font-weight-semibold)",
                  background: platformOwner
                    ? "color-mix(in oklab, var(--gt-sys-success, #10b981) 18%, transparent)"
                    : "color-mix(in oklab, var(--gt-sys-warning, #f59e0b) 22%, transparent)",
                  color: platformOwner ? "var(--gt-sys-success, #10b981)" : "var(--gt-sys-text)",
                }}
              >
                {platformOwner
                  ? t("admin.permissions_diagnostics.current_session.platform_owner_yes")
                  : t("admin.permissions_diagnostics.current_session.platform_owner_no")}
              </span>
            </Inline>
            <Stack gap={0}>
              <span
                style={{
                  fontSize: "var(--gt-sys-font-size-sm)",
                  fontWeight: "var(--gt-sys-font-weight-semibold)",
                }}
              >
                {t("admin.permissions_diagnostics.current_session.roles_label")}
              </span>
              <Inline gap={1} wrap data-testid="admin-perm-current-session-roles">
                {session.roles.length === 0 ? (
                  <span
                    style={{
                      fontSize: "var(--gt-sys-font-size-xs)",
                      color: "var(--gt-sys-text-muted)",
                      fontStyle: "italic",
                    }}
                  >
                    {t("admin.permissions_diagnostics.current_session.roles_empty")}
                  </span>
                ) : (
                  session.roles.map((r) => (
                    <code
                      key={r}
                      style={{
                        padding: "2px 8px",
                        borderRadius: 999,
                        fontSize: "var(--gt-sys-font-size-xs)",
                        background: "color-mix(in oklab, var(--gt-sys-accent) 12%, transparent)",
                        color: "var(--gt-sys-accent)",
                      }}
                    >
                      {r}
                    </code>
                  ))
                )}
              </Inline>
            </Stack>
            {!platformOwner ? (
              <p
                data-testid="admin-perm-current-session-hint"
                style={{
                  color: "var(--gt-sys-text-muted)",
                  fontSize: "var(--gt-sys-font-size-xs)",
                  margin: 0,
                  paddingBlockStart: "var(--gt-sys-space-1)",
                  fontStyle: "italic",
                }}
              >
                {t("admin.permissions_diagnostics.current_session.platform_owner_hint")}
              </p>
            ) : null}
          </Stack>
        </Stack>
      </Card>
      <Stack gap={2} data-testid="admin-permissions-diagnostics-content">
        {roles.map((role) => (
          <Card key={role.id} elevation={1}>
            <Stack gap={2}>
              <Inline justify="space-between" align="center" wrap>
                <strong>{role.label}</strong>
                <code style={{ fontSize: "var(--gt-sys-font-size-xs)" }}>{role.id}</code>
              </Inline>
              <Inline gap={1} wrap>
                {role.permissions.map((p) => (
                  <span
                    key={p}
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: "var(--gt-sys-font-size-xs)",
                      fontWeight: "var(--gt-sys-font-weight-medium)",
                      background: "color-mix(in oklab, var(--gt-sys-accent) 12%, transparent)",
                      color: "var(--gt-sys-accent)",
                    }}
                  >
                    {p}
                  </span>
                ))}
              </Inline>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

function SessionRow(props: { label: string; value: string; testId: string; mono?: boolean }) {
  return (
    <Inline gap={2} align="baseline" wrap>
      <span
        style={{
          fontSize: "var(--gt-sys-font-size-sm)",
          fontWeight: "var(--gt-sys-font-weight-semibold)",
          minInlineSize: 110,
        }}
      >
        {props.label}
      </span>
      {props.mono ? (
        <code
          data-testid={props.testId}
          style={{
            fontSize: "var(--gt-sys-font-size-xs)",
            fontFamily: "var(--gt-sys-font-family-mono, monospace)",
            wordBreak: "break-all",
          }}
        >
          {props.value}
        </code>
      ) : (
        <span data-testid={props.testId} style={{ fontSize: "var(--gt-sys-font-size-sm)" }}>
          {props.value}
        </span>
      )}
    </Inline>
  );
}
